import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Truck, Calendar, Clock, MapPin, User, FileText,
  Download, Loader2, Upload, AlertCircle, Eye, X, ImageIcon, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const REASONS = [
  { value: 'puncture_flat_tyre',   label: 'Puncture / Flat Tyre' },
  { value: 'accident',             label: 'Accident' },
  { value: 'rta_vehicle_testing',  label: 'For RTA Vehicle Testing' },
  { value: 'absconding_rider',     label: 'Recovery from Absconding Rider' },
  { value: 'lessee_request',       label: 'Recovery upon request from Lessee' },
  { value: 'functional_defect',    label: 'Functional Defect (not attributable)' },
  { value: 'others',               label: 'Others' },
];

const REASON_BADGE = {
  puncture_flat_tyre: 'bg-blue-100 text-blue-700',
  accident: 'bg-red-100 text-red-700',
  rta_vehicle_testing: 'bg-purple-100 text-purple-700',
  absconding_rider: 'bg-amber-100 text-amber-700',
  lessee_request: 'bg-teal-100 text-teal-700',
  functional_defect: 'bg-orange-100 text-orange-700',
  others: 'bg-slate-100 text-slate-700',
};

// File URLs may be either relative (`/api/uploads/...`) or absolute (e.g.
// SharePoint links stored in the database). Pass absolute URLs through
// unchanged; only prefix relative paths with the backend host.
const FILE_URL = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${process.env.REACT_APP_BACKEND_URL}${path}`;
};

const todayDate = () => new Date().toISOString().slice(0, 10);
const nowTime  = () => new Date().toTimeString().slice(0, 5);

const blankForm = () => ({
  bike_id: '',
  recovery_date: todayDate(),
  recovery_time: nowTime(),
  reason: '',
  reason_description: '',
  police_report_url: '',
  police_report_type: '',
  driver_name: '',
  driver_phone: '',
  recovery_company: '',
  invoice_url: '',
  pickup_location: '',
  dropoff_location: '',
  pickup_photos: [],
  dropoff_photos: [],
  recovery_cost: '',
  person_in_charge_user_id: '',
  person_in_charge_name: '',
  // Manual rider fields (only used if selected bike has no rider)
  rider_id: '',
  rider_name: '',
  rider_unique_id: '',
  platform: '',
  platform_id: '',
  rider_added_manually: false,
  // Manual lessee (used if bike + rider have no lessee linked in DB)
  lessee_id: '',
  lessee_name: '',
  lessee_added_manually: false,
});

export default function RecoveryVehicle() {
  const navigate = useNavigate();
  const [recoveries, setRecoveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [eligibleBikes, setEligibleBikes] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(blankForm());
  const [bikeInfo, setBikeInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({}); // {invoice: bool, police_report: bool, pickup: bool, dropoff: bool}

  // -------- Load log --------
  const fetchRecoveries = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recoveries`, { headers: auth() });
      setRecoveries(r.data.recoveries || []);
    } catch (e) {
      toast.error('Failed to load recoveries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecoveries(); }, []);

  const handleOpenAddDialog = async () => {
    setOpenForm(true);
    try {
      const [b, u] = await Promise.all([
        axios.get(`${API}/recoveries/eligible-bikes`, { headers: auth() }),
        axios.get(`${API}/recoveries/users`,          { headers: auth() }),
      ]);
      setEligibleBikes(b.data.bikes || []);
      setUsers(u.data.users || []);
      setForm(blankForm());
      setBikeInfo(null);
    } catch (e) {
      toast.error('Failed to load form data');
    }
  };

  // -------- Auto-fill on bike selection --------
  const onBikeChange = async (bike_id) => {
    setForm((f) => ({ ...f, bike_id, rider_id: '', rider_name: '', rider_unique_id: '', platform: '', platform_id: '', rider_added_manually: false, lessee_id: '', lessee_name: '', lessee_added_manually: false }));
    setBikeInfo(null);
    if (!bike_id) return;
    try {
      const r = await axios.get(`${API}/recoveries/bike-info/${bike_id}`, { headers: auth() });
      setBikeInfo(r.data);
      const next = {};
      if (r.data.rider) {
        next.rider_id = r.data.rider.id || '';
        next.rider_name = r.data.rider.name || '';
        next.rider_unique_id = r.data.rider.rider_unique_id || '';
        next.platform = r.data.rider.platform || '';
        next.platform_id = r.data.rider.platform_id || '';
        next.rider_added_manually = false;
      } else {
        next.rider_added_manually = true; // typed manually
      }
      if (r.data.lessee && r.data.lessee.name) {
        next.lessee_id = r.data.lessee.id || '';
        next.lessee_name = r.data.lessee.name;
        next.lessee_added_manually = false;
      } else {
        next.lessee_added_manually = true; // user will type
      }
      setForm((f) => ({ ...f, ...next }));
    } catch (e) {
      toast.error('Failed to fetch bike info');
    }
  };

  // -------- Upload helpers --------
  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const r = await axios.post(`${API}/recoveries/upload`, fd, {
      headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
    });
    return r.data.file_url;
  };

  const handleSingleUpload = async (e, field, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const url = await uploadFile(file, kind);
      setForm((f) => ({ ...f, [field]: url }));
      toast.success(`${kind.replace('_', ' ')} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
      e.target.value = '';
    }
  };

  const handleMultiUpload = async (e, field, kind) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if ((form[field]?.length || 0) + files.length > 5) {
      toast.error('Maximum 5 photos per side');
      return;
    }
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const urls = [];
      for (const f of files) {
        const u = await uploadFile(f, kind);
        urls.push(u);
      }
      setForm((f) => ({ ...f, [field]: [...(f[field] || []), ...urls] }));
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
      e.target.value = '';
    }
  };

  const removePhoto = (field, idx) => {
    setForm((f) => ({ ...f, [field]: f[field].filter((_, i) => i !== idx) }));
  };

  // -------- Submit --------
  const handleSubmit = async () => {
    if (!form.bike_id) { toast.error('Please select a bike'); return; }
    if (!form.reason)  { toast.error('Please select a reason'); return; }
    if (form.reason === 'accident' && (!form.police_report_url || !form.police_report_type)) {
      toast.error('Police report upload and type are required for Accident');
      return;
    }
    if (['functional_defect', 'others'].includes(form.reason) && !form.reason_description.trim()) {
      toast.error('Please add a description for the selected reason');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/recoveries`, form, { headers: auth() });
      toast.success('Recovery logged successfully');
      setOpenForm(false);
      fetchRecoveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save recovery');
    } finally {
      setSubmitting(false);
    }
  };

  // -------- Open detail --------
  const openDetailDialog = async (id) => {
    try {
      const r = await axios.get(`${API}/recoveries/${id}`, { headers: auth() });
      setSelectedDetail(r.data);
      setOpenDetail(true);
    } catch (e) {
      toast.error('Failed to load recovery details');
    }
  };

  const selectedBike = eligibleBikes.find((b) => b.id === form.bike_id);
  const showRiderManual = form.bike_id && bikeInfo && !bikeInfo.rider;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/motorcycles')} data-testid="recovery-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Truck className="w-7 h-7 text-amber-600" /> Recovery Vehicle
            </h1>
            <p className="text-sm text-slate-500">Log every motorcycle recovery — punctures, accidents, RTA testing, absconding, lessee requests and more.</p>
          </div>
        </div>
        <Button onClick={handleOpenAddDialog} className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="add-recovery-btn">
          <Plus className="w-4 h-4" /> Add Recovery Vehicle
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-amber-200 bg-amber-50/40">
          <p className="text-xs text-amber-900/70">Total Recoveries</p>
          <p className="text-2xl font-bold text-amber-900" data-testid="recovery-stat-total">{recoveries.length}</p>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50/40">
          <p className="text-xs text-red-900/70">Accident Recoveries</p>
          <p className="text-2xl font-bold text-red-900" data-testid="recovery-stat-accident">
            {recoveries.filter((r) => r.reason === 'accident').length}
          </p>
        </Card>
        <Card className="p-4 border-blue-200 bg-blue-50/40">
          <p className="text-xs text-blue-900/70">Punctures</p>
          <p className="text-2xl font-bold text-blue-900" data-testid="recovery-stat-puncture">
            {recoveries.filter((r) => r.reason === 'puncture_flat_tyre').length}
          </p>
        </Card>
        <Card className="p-4 border-teal-200 bg-teal-50/40">
          <p className="text-xs text-teal-900/70">Lessee / Absconding</p>
          <p className="text-2xl font-bold text-teal-900" data-testid="recovery-stat-other">
            {recoveries.filter((r) => ['lessee_request', 'absconding_rider'].includes(r.reason)).length}
          </p>
        </Card>
      </div>

      {/* Log table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-600" /> Recovery Log
            <Badge variant="secondary" className="ml-2">{recoveries.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            </div>
          ) : recoveries.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">
              No recoveries logged yet. Click <span className="font-medium text-amber-700">Add Recovery Vehicle</span> to log the first one.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Plate</TableHead>
                    <TableHead>Rider ID</TableHead>
                    <TableHead>Rider Name</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Platform ID</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Cost (AED)</TableHead>
                    <TableHead>Person In-charge</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recoveries.map((r, i) => (
                    <TableRow
                      key={r.id}
                      className="text-sm hover:bg-amber-50/30 cursor-pointer"
                      onClick={() => openDetailDialog(r.id)}
                      data-testid={`recovery-row-${i}`}
                    >
                      <TableCell className="font-mono text-xs">{r.recovery_date || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.recovery_time || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="font-mono">{r.bike_plate || '-'}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{r.rider_unique_id || '-'}</TableCell>
                      <TableCell>
                        {r.rider_name || <span className="text-slate-400">—</span>}
                        {r.rider_added_manually && (
                          <Badge variant="outline" className="ml-2 text-[10px] bg-yellow-50 text-yellow-700 border-yellow-200">
                            Added Manually
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{r.platform || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.platform_id || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] ${REASON_BADGE[r.reason] || ''}`}>{r.reason_label || r.reason}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs" data-testid={`recovery-cost-${i}`}>
                        {r.recovery_cost ? Number(r.recovery_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                      </TableCell>
                      <TableCell>{r.person_in_charge_name || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); openDetailDialog(r.id); }} data-testid={`recovery-view-${i}`}>
                          <Eye className="w-4 h-4 text-amber-700" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ ADD RECOVERY DIALOG ============ */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="recovery-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" /> Add Vehicle Recovery
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Bike selection */}
            <Card className="p-4 border-amber-200">
              <h4 className="text-sm font-semibold text-amber-900 mb-3">Bike & Rider</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Bike (Plate No.) <span className="text-red-500">*</span></Label>
                  <Select value={form.bike_id} onValueChange={onBikeChange}>
                    <SelectTrigger data-testid="recovery-bike-select"><SelectValue placeholder="Select bike..." /></SelectTrigger>
                    <SelectContent>
                      {eligibleBikes.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.plate_number} {b.has_rider ? `· ${b.rider_name}` : '· (no rider)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    Lessee
                    {form.lessee_added_manually && form.bike_id && (
                      <Badge variant="outline" className="ml-2 text-[9px] bg-yellow-50 text-yellow-700 border-yellow-200">manual</Badge>
                    )}
                  </Label>
                  <Input
                    value={form.lessee_name || ''}
                    onChange={(e) => setForm((f) => ({ ...f, lessee_name: e.target.value, lessee_added_manually: true }))}
                    readOnly={!!(bikeInfo?.lessee?.name)}
                    className={bikeInfo?.lessee?.name ? 'bg-slate-50' : ''}
                    placeholder={form.bike_id ? (form.lessee_added_manually ? 'Type lessee name…' : 'auto-filled') : 'auto-filled'}
                    data-testid="recovery-lessee-input"
                  />
                </div>
              </div>

              {/* Rider info */}
              {!showRiderManual ? (
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Rider Name</Label>
                    <Input value={form.rider_name || ''} readOnly className="bg-slate-50" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rider ID</Label>
                    <Input value={form.rider_unique_id || ''} readOnly className="bg-slate-50" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Platform</Label>
                    <Input value={`${form.platform || ''}${form.platform_id ? ` (${form.platform_id})` : ''}`} readOnly className="bg-slate-50" />
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-3 rounded-md border border-yellow-200 bg-yellow-50">
                  <p className="text-xs text-yellow-800 flex items-center gap-1 mb-2">
                    <AlertCircle className="w-3 h-3" /> This bike has no assigned rider — please enter rider details manually. Log will be marked &ldquo;Added Manually&rdquo;.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="Rider Name" value={form.rider_name}
                           onChange={(e) => setForm((f) => ({ ...f, rider_name: e.target.value, rider_added_manually: true }))} data-testid="recovery-manual-rider-name" />
                    <Input placeholder="Rider ID (e.g. EMIX0023)" value={form.rider_unique_id}
                           onChange={(e) => setForm((f) => ({ ...f, rider_unique_id: e.target.value, rider_added_manually: true }))} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Platform" value={form.platform}
                             onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value, rider_added_manually: true }))} />
                      <Input placeholder="Platform ID" value={form.platform_id}
                             onChange={(e) => setForm((f) => ({ ...f, platform_id: e.target.value, rider_added_manually: true }))} />
                    </div>
                  </div>
                </div>
              )}

              {/* Document downloads */}
              {bikeInfo && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <DocLinkButton url={bikeInfo.bike?.registration_card_url} label="Registration Card" />
                  <DocLinkButton url={bikeInfo.rider?.uae_driving_license_url} label="Driving License" />
                  <DocLinkButton url={bikeInfo.rider?.emirates_id_url} label="Emirates ID" />
                </div>
              )}
            </Card>

            {/* Date / Time / Reason */}
            <Card className="p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">When & Why</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Date <span className="text-red-500">*</span></Label>
                  <Input type="date" value={form.recovery_date} onChange={(e) => setForm((f) => ({ ...f, recovery_date: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Time <span className="text-red-500">*</span></Label>
                  <Input type="time" value={form.recovery_time} onChange={(e) => setForm((f) => ({ ...f, recovery_time: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Reason <span className="text-red-500">*</span></Label>
                  <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v, reason_description: '', police_report_url: '', police_report_type: '' }))}>
                    <SelectTrigger data-testid="recovery-reason-select"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                    <SelectContent>{REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Conditional: Accident */}
              {form.reason === 'accident' && (
                <div className="mt-3 p-3 rounded-md border border-red-200 bg-red-50">
                  <p className="text-xs text-red-800 mb-2 font-medium">Accident — extra info required</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Police Report Type <span className="text-red-500">*</span></Label>
                      <Select value={form.police_report_type} onValueChange={(v) => setForm((f) => ({ ...f, police_report_type: v }))}>
                        <SelectTrigger data-testid="recovery-police-type"><SelectValue placeholder="Red / Green" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="red">Red Report</SelectItem>
                          <SelectItem value="green">Green Report</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Upload Police Report <span className="text-red-500">*</span></Label>
                      <div className="flex items-center gap-2">
                        <Input type="file" accept="application/pdf,image/*" disabled={uploading.police_report}
                               onChange={(e) => handleSingleUpload(e, 'police_report_url', 'police_report')}
                               data-testid="recovery-police-upload" />
                        {form.police_report_url && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Conditional: description for functional_defect / others */}
              {['functional_defect', 'others'].includes(form.reason) && (
                <div className="mt-3 space-y-1">
                  <Label className="text-xs">Description <span className="text-red-500">*</span></Label>
                  <Textarea rows={2} value={form.reason_description}
                            onChange={(e) => setForm((f) => ({ ...f, reason_description: e.target.value }))}
                            placeholder="Describe the defect or reason in detail..."
                            data-testid="recovery-reason-desc" />
                </div>
              )}
            </Card>

            {/* Recovery vehicle details */}
            <Card className="p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Recovery Vehicle Details</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Driver Name</Label>
                  <Input value={form.driver_name} onChange={(e) => setForm((f) => ({ ...f, driver_name: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Driver Phone</Label>
                  <Input value={form.driver_phone} onChange={(e) => setForm((f) => ({ ...f, driver_phone: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Recovery Company</Label>
                  <Input value={form.recovery_company} onChange={(e) => setForm((f) => ({ ...f, recovery_company: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pick-up Location</Label>
                  <Input value={form.pickup_location} onChange={(e) => setForm((f) => ({ ...f, pickup_location: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Drop-off Location</Label>
                  <Input value={form.dropoff_location} onChange={(e) => setForm((f) => ({ ...f, dropoff_location: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Recovery Cost (AED)</Label>
                  <Input type="number" step="0.01" min="0" value={form.recovery_cost}
                         onChange={(e) => setForm((f) => ({ ...f, recovery_cost: e.target.value }))}
                         placeholder="0.00"
                         data-testid="recovery-cost-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Invoice (PDF / Image)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="application/pdf,image/*" disabled={uploading.invoice}
                           onChange={(e) => handleSingleUpload(e, 'invoice_url', 'invoice')}
                           data-testid="recovery-invoice-upload" />
                    {form.invoice_url && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                {[
                  { field: 'pickup_photos',  label: 'Pick-up Photos',  kind: 'pickup_photo' },
                  { field: 'dropoff_photos', label: 'Drop-off Photos', kind: 'dropoff_photo' },
                ].map(({ field, label, kind }) => (
                  <div key={field} className="space-y-2">
                    <Label className="text-xs">{label} (max 5, 15 MB each)</Label>
                    <div className="flex items-center gap-2">
                      <Input type="file" accept="image/*" multiple disabled={uploading[kind]}
                             onChange={(e) => handleMultiUpload(e, field, kind)}
                             data-testid={`recovery-${kind}-upload`} />
                      {uploading[kind] && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    {(form[field] || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {form[field].map((url, idx) => (
                          <div key={idx} className="relative inline-block">
                            <a href={FILE_URL(url)} target="_blank" rel="noreferrer">
                              <img src={FILE_URL(url)} alt={`${label} ${idx + 1}`} className="w-16 h-16 object-cover rounded border" />
                            </a>
                            <button type="button" onClick={() => removePhoto(field, idx)}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Person in charge */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Person In-charge</Label>
                  <Select value={form.person_in_charge_user_id} onValueChange={(v) => {
                    const u = users.find((x) => x.id === v);
                    setForm((f) => ({ ...f, person_in_charge_user_id: v, person_in_charge_name: u?.name || '' }));
                  }}>
                    <SelectTrigger data-testid="recovery-pic-select"><SelectValue placeholder="Select user..." /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} {u.role && <span className="text-xs text-slate-400 ml-1">({u.role})</span>}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="recovery-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save Recovery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DETAIL VIEW DIALOG ============ */}
      <Dialog open={openDetail} onOpenChange={setOpenDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recovery-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-amber-600" /> Recovery Details — {selectedDetail?.bike_plate}
            </DialogTitle>
          </DialogHeader>
          {selectedDetail && <RecoveryDetailContent r={selectedDetail} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecoveryDetailContent({ r }) {
  const docs = r.live_documents || {};
  return (
    <div className="space-y-4 pt-2 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Info label="Date" value={r.recovery_date} />
        <Info label="Time" value={r.recovery_time} />
        <Info label="Plate" value={r.bike_plate} mono />
        <Info label="Reason">
          <Badge className={`text-[10px] ${REASON_BADGE[r.reason] || ''}`}>{r.reason_label}</Badge>
        </Info>

        <Info label="Rider ID" value={r.rider_unique_id} mono />
        <Info label="Rider Name">
          <div>
            {r.rider_name || <span className="text-slate-400">—</span>}
            {r.rider_added_manually && (
              <Badge variant="outline" className="ml-2 text-[9px] bg-yellow-50 text-yellow-700 border-yellow-200">Added Manually</Badge>
            )}
          </div>
        </Info>
        <Info label="Platform" value={r.platform} />
        <Info label="Platform ID" value={r.platform_id} mono />

        <Info label="Lessee" value={r.lessee_name} />
        <Info label="Person In-charge" value={r.person_in_charge_name} />
      </div>

      {(r.reason === 'accident') && (
        <Card className="p-3 border-red-200 bg-red-50/40">
          <p className="text-xs font-semibold text-red-800 mb-2">Accident details</p>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={`${r.police_report_type === 'red' ? 'border-red-300 text-red-700' : 'border-green-300 text-green-700'}`}>
              {r.police_report_type === 'red' ? 'Red Report' : r.police_report_type === 'green' ? 'Green Report' : '—'}
            </Badge>
            <DocLinkButton url={r.police_report_url} label="Police Report" />
          </div>
        </Card>
      )}

      {r.reason_description && (
        <Card className="p-3"><p className="text-xs font-semibold mb-1">Description</p><p className="text-sm">{r.reason_description}</p></Card>
      )}

      <Card className="p-3">
        <p className="text-xs font-semibold mb-2">Recovery Vehicle</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Info label="Driver" value={r.driver_name} />
          <Info label="Phone" value={r.driver_phone} />
          <Info label="Company" value={r.recovery_company} />
          <Info label="Pick-up" value={r.pickup_location} />
          <Info label="Drop-off" value={r.dropoff_location} />
        </div>
        {r.invoice_url && (
          <div className="mt-2">
            <DocLinkButton url={r.invoice_url} label="Invoice" />
          </div>
        )}
      </Card>

      {/* Photos */}
      {(r.pickup_photos?.length || r.dropoff_photos?.length) && (
        <div className="grid grid-cols-2 gap-3">
          {[{ label: 'Pick-up Photos', list: r.pickup_photos }, { label: 'Drop-off Photos', list: r.dropoff_photos }]
            .filter((g) => g.list?.length).map((g) => (
              <Card key={g.label} className="p-3">
                <p className="text-xs font-semibold mb-2 flex items-center gap-1"><ImageIcon className="w-3 h-3" />{g.label}</p>
                <div className="flex flex-wrap gap-1">
                  {g.list.map((url, i) => (
                    <a key={i} href={FILE_URL(url)} target="_blank" rel="noreferrer">
                      <img src={FILE_URL(url)} alt="" className="w-16 h-16 object-cover rounded border hover:ring-2 hover:ring-amber-400" />
                    </a>
                  ))}
                </div>
              </Card>
            ))}
        </div>
      )}

      {/* Live document downloads */}
      <Card className="p-3 border-blue-100 bg-blue-50/40">
        <p className="text-xs font-semibold mb-2 text-blue-900">Linked Documents</p>
        <div className="flex flex-wrap gap-2">
          <DocLinkButton url={docs.registration_card_url} label="Registration Card" />
          <DocLinkButton url={docs.uae_driving_license_url} label="Driving License" />
          <DocLinkButton url={docs.emirates_id_url} label="Emirates ID" />
          {!(docs.registration_card_url || docs.uae_driving_license_url || docs.emirates_id_url) && (
            <p className="text-xs text-slate-400">No linked documents available.</p>
          )}
        </div>
      </Card>
    </div>
  );
}

function Info({ label, value, children, mono = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      {children || <p className={`text-sm ${mono ? 'font-mono' : ''}`}>{value || <span className="text-slate-400">—</span>}</p>}
    </div>
  );
}

/**
 * Document link button — proper <a> styled like a button (no nested <button>
 * elements which Chrome treats as popup → about:blank#blocked).
 *
 * Renders as a plain anchor that triggers download. For external URLs
 * (e.g. SharePoint) we also expose a "Copy" affordance so the user can
 * paste the link into a tab where they're already authenticated, in case
 * Chrome's popup blocker interferes.
 */
function DocLinkButton({ url, label }) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Sanitise: only allow http(s) absolute OR relative path starting with /
  const isAbsolute = /^https?:\/\//i.test(trimmed);
  const isRelative = trimmed.startsWith('/');
  if (!isAbsolute && !isRelative) return null;

  const fullUrl = isAbsolute ? trimmed : `${process.env.REACT_APP_BACKEND_URL}${trimmed}`;
  const baseClasses = 'inline-flex items-center gap-1 text-xs h-8 px-3 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors';

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="inline-flex items-stretch rounded-md overflow-hidden shadow-sm">
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
        className={`${baseClasses} rounded-r-none border-r-0`}
        data-testid={`doc-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Download className="w-3 h-3" /> {label}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center px-2 rounded-md rounded-l-none border border-slate-300 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-700"
        title="Copy link to clipboard"
        data-testid={`doc-link-copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <FileText className="w-3 h-3" />
      </button>
    </div>
  );
}
